import { Activity, File, Menu, PanelLeftClose, PanelLeftOpen, PanelRightOpen } from "lucide-react";
import type { ReactNode } from "react";
import type { ResultDocument } from "@rnavoyager/contracts";
import { SessionMarkdown } from "./SessionFileReferences";
import { TaskResultDocumentView } from "./TaskResultDocument";
import type { ResultSelection } from "./TaskResultDocument";
import type { MessageAttachment, TaskEvent } from "./types";

/** 正式会话与游客案例共用外观，身份和实际动作由各自的数据源负责。 */
export function WorkspaceSidebar({
  collapsed,
  mobileOpen,
  onToggleCollapsed,
  onToggleMobile,
  children,
}: {
  collapsed: boolean;
  mobileOpen: boolean;
  onToggleCollapsed: () => void;
  onToggleMobile: () => void;
  children: ReactNode;
}) {
  return (
    <aside
      className={["sidebar", collapsed ? "collapsed" : "", mobileOpen ? "mobile-open" : ""]
        .filter(Boolean)
        .join(" ")}
    >
      <button
        aria-expanded={mobileOpen}
        aria-label={mobileOpen ? "关闭任务菜单" : "打开任务菜单"}
        className="mobile-sidebar-menu-toggle"
        onClick={onToggleMobile}
      >
        <Menu size={18} />
        <span>任务</span>
      </button>
      <div className="sidebar-brand">
        <div className="brand-mark small">
          <Activity size={18} />
        </div>
        <div className="sidebar-identity">
          <strong>RNAVoyager</strong>
          <span>计算研究</span>
        </div>
        <button
          aria-label={collapsed ? "展开侧栏" : "收起侧栏"}
          className="icon-button sidebar-toggle"
          onClick={onToggleCollapsed}
          title={collapsed ? "展开侧栏" : "收起侧栏"}
        >
          {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        </button>
      </div>
      {children}
    </aside>
  );
}

export function TaskWorkspaceHeader({
  title,
  identity,
  actions,
}: {
  title: string;
  identity?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="task-header">
      <div>
        <p className="eyebrow">AGENTIC BIOINFORMATICS</p>
        <h1 aria-live="polite">{title}</h1>
        {identity && <p className="session-identity">{identity}</p>}
      </div>
      {actions && <div className="header-actions">{actions}</div>}
    </header>
  );
}

export function ChatMessage({
  role,
  label,
  time,
  attachments = [],
  children,
}: {
  role: "user" | "system";
  label: string;
  time?: string;
  attachments?: readonly MessageAttachment[];
  children: ReactNode;
}) {
  return (
    <article className={`message ${role}`}>
      <div className="message-meta">
        {label}
        {time && <time>{time}</time>}
      </div>
      {attachments.length > 0 && (
        <div aria-label="本条消息的附件" className="message-attachments">
          {attachments.map((attachment) => (
            <span className="message-attachment" key={attachment.path} title={attachment.name}>
              <File aria-hidden="true" size={16} />
              <span>{attachment.name}</span>
            </span>
          ))}
        </div>
      )}
      {children}
    </article>
  );
}

export function TaskResultCard({
  document,
  events,
  inlineFeatured,
  onOpenResult,
  onUseSelection,
  sessionId,
  summary,
  publicDemo = false,
}: {
  document: ResultDocument | null;
  events?: readonly TaskEvent[];
  inlineFeatured: boolean;
  onOpenResult?: (documentId: string) => void;
  onUseSelection?: (selection: ResultSelection) => void;
  sessionId: string;
  summary: string;
  publicDemo?: boolean;
}) {
  const resultLabel = document?.title === "进行中的结果" ? "进行中任务的结果" : "已完成任务的结果";
  return (
    <section aria-label={resultLabel} className="result-card">
      <header className="result-card-heading">
        <h2>{document?.title ?? "任务总结"}</h2>
        {document && onOpenResult && (
          <button
            aria-label={`在结果工作台中打开${document.title}`}
            className="secondary result-workspace-open"
            onClick={() => onOpenResult(document.documentId)}
            type="button"
          >
            <PanelRightOpen size={15} />
            打开结果工作台
          </button>
        )}
      </header>
      {document ? (
        <TaskResultDocumentView
          document={document}
          events={events}
          inlineFeatured={inlineFeatured}
          onUseSelection={onUseSelection}
          sessionId={sessionId}
          publicDemo={publicDemo}
        />
      ) : (
        <SessionMarkdown sessionId={sessionId}>{summary}</SessionMarkdown>
      )}
    </section>
  );
}

export interface TaskPlanDisplayStep {
  id: string;
  kind: string;
  description: string;
  acceptanceCriteria?: string;
  toolLabel?: string;
}

export function TaskPlanCardView({
  objective,
  preview,
  steps,
  assumptions = [],
  checkpoints = [],
  awaitingStart,
  status,
  busy,
  boundary,
  startHint,
  startDescriptionId,
  onStart,
  onDiscard,
}: {
  objective: string;
  preview: string;
  steps: readonly TaskPlanDisplayStep[];
  assumptions?: readonly string[];
  checkpoints?: readonly string[];
  awaitingStart: boolean;
  status: { label: string; tone: string };
  busy: boolean;
  boundary?: ReactNode;
  startHint?: ReactNode;
  startDescriptionId?: string;
  onStart?: () => void | Promise<void>;
  onDiscard?: () => void | Promise<void>;
}) {
  return (
    <section className="plan-card interaction-card">
      <div className="plan-record-heading">
        <p className="eyebrow">{awaitingStart ? "计划模式 · 等待开始执行" : "动态任务规划记录"}</p>
        <span className={`plan-record-status ${status.tone}`}>{status.label}</span>
      </div>
      <h2>{objective}</h2>
      <p>{preview}</p>
      <ol className="plan-steps">
        {steps.map((step) => (
          <li key={step.id}>
            <span>{step.kind}</span>
            <div>
              <strong>{step.description}</strong>
              {step.acceptanceCriteria && <p>验收：{step.acceptanceCriteria}</p>}
              {step.toolLabel && <code>{step.toolLabel}</code>}
            </div>
          </li>
        ))}
      </ol>
      {assumptions.length > 0 && (
        <div className="plan-notes">
          <strong>假设：</strong>
          {assumptions.join("；")}
        </div>
      )}
      {checkpoints.length > 0 && (
        <div className="plan-notes">
          <strong>人工检查：</strong>
          {checkpoints.join("；")}
        </div>
      )}
      {boundary}
      {awaitingStart && startHint}
      {awaitingStart && (
        <div className="plan-actions">
          <button className="secondary" disabled={busy} onClick={() => void onDiscard?.()}>
            放弃计划
          </button>
          <button
            className="primary"
            aria-describedby={startDescriptionId}
            disabled={busy}
            onClick={() => void onStart?.()}
          >
            开始执行
          </button>
        </div>
      )}
    </section>
  );
}
